import './BookingConfirmationMacroComponent.tsx.css';

import '../../../../js/loadSyncfusion';

 import { HtmlEditor, Image, Inject, Link, QuickToolbar, RichTextEditorComponent, Toolbar } from '@syncfusion/ej2-react-richtexteditor';

import { isReservationExistingPerson, isReservationNewPerson, multiplyAndRemoveDecimal, PersonDto, ReservationPerson, ReservationState, getDisplayName, ReservationPosItem } from "@digitalopsdev/serviceops-reservations-components/dist/EditReservation/model"; // Added getDisplayName, ReservationPosItem
 import { getProgramTypeDisplayName, getRoomTypeDisplayName } from "@digitalopsdev/serviceops-reservations-components/dist/EditReservation/OrderDetailsComponent";
 import { differenceInHours, format, startOfDay } from 'date-fns';
 import { isReservationQuoteStatus, ReservationQuoteStatus } from '../ReservationStatus';
 import { ReactNode, useState, useEffect, useRef } from 'react'; // Added useState, useEffect, useRef
 import { countProgramDaysBetween, decimalToTHBWithPrefix, longToMoney, longToTHBWithPrefix } from '../../../../js/utils';
 import React from 'react';
 import { useModelState } from '../../../../js/useModelState'; // Added useModelState
 import { MacroCopyButtons } from './MacroCopyButtons';
 import { programOperatingDays } from '../../../../js/programOperatingDays';
 import { useConfirmationState } from './useConfirmationState';

export function BookingConfirmationMacroComponent({ reservationState }: BookingConfirmationMacroComponentProps) {
    useConfirmationState(reservationState);
    // Ensure component re-renders if reservationState.Person changes
    useModelState(reservationState.Person);

    // State for the asynchronously fetched first name
    const [firstName, setFirstName] = useState<string>("Loading name...");
    
    // Ref for the RichTextEditor
    const rteRef = useRef<RichTextEditorComponent>(null);

    // Effect to fetch display name and extract first name
    useEffect(() => {
        const person = reservationState.Person.value;
        if (person) {
            setFirstName("Loading name..."); // Show loading state
            getDisplayName(person, reservationState)
                .then(name => {
                    // Extract first name (assuming format "First Last" or just "First")
                    const nameParts = (name || "").split(" ");
                    setFirstName(nameParts[0] || "Client"); // Use "Client" as fallback if name is empty/error
                })
                .catch(error => {
                    console.error("Error fetching display name in BookingConfirmationMacro:", error);
                    setFirstName("Client"); // Fallback on error
                });
        } else {
            setFirstName("Client"); // Default if no person selected
        }
    }, [reservationState.Person.value]); // Dependency on person value

    // Removed internal getFirstName function

    // Removed direct assignment: let firstName = getFirstName(reservationState.Person.value);

    // Check if still loading, handle potential error state if needed
    if (firstName === "Loading name...") {
         return <div>Loading booking details...</div>; // Or a spinner
    }
    // Removed check: if (!firstName) { return <div>Unknown person</div>; } - Handled by fallback

    // Removed: firstName = firstName.trim(); - Handled in useEffect

    const programs = reservationState.ProgramBookings.toArray();

    const transformedPrograms = {} as Record<string, {
        programName: string,
        programDays: number,
        closureNote: string,
        programStartDate: string,
        programEndDate: string,
        programPrice: number,
        quantity: number
    }>;

    for (const program of programs) {

        const programName = getProgramTypeDisplayName(reservationState, program.ProgramType.value);
        const programPrice = multiplyAndRemoveDecimal(program.Price.price.value.payableAmount);
        const programDays = countProgramDaysBetween(program.Start.value, program.End.value);
        const programStartDate = format(program.Start.value, 'MMMM d, yyyy');
        const programEndDate = format(program.End.value, 'MMMM d, yyyy');

        const programStartOfDay = startOfDay(program.Start.value);

        const groupKey = `${programName}_${programStartDate}_${programEndDate}`;

        const existingTransformedProgram = transformedPrograms[groupKey];

        if (!existingTransformedProgram) {
            transformedPrograms[groupKey] = {
                programName,
                programDays,
                closureNote: programOperatingDays(program.Start.value, program.End.value).closureNote,
                programStartDate,
                programEndDate,
                programPrice,
                quantity: 1
            };
        } else {
            existingTransformedProgram.programPrice += programPrice;
            existingTransformedProgram.quantity += 1;
        }
    }

    const programsEle = Object.entries(transformedPrograms).map(([, program]) => {
        return (
            <React.Fragment key={program.programName}>
                <div key={program.programName}>
                    <p>
                        {program.quantity <= 1 && (
                            <>
                                <strong>Program:</strong> {program.programName}
                            </>
                        )}
                        {program.quantity > 1 && (
                            <>
                                <strong>Program:</strong> {program.quantity} x {program.programName}
                            </>
                        )}
                        <br />
                        <strong>Duration:</strong> {program.programDays} program {program.programDays === 1 ? 'day' : 'days'}
                        {program.closureNote && <><br />{program.closureNote}</>}
                        <br />
                        <strong>Program start date: </strong> {program.programStartDate}
                        <br />
                        <strong>Program end date:</strong> {program.programEndDate}
                        <br />
                        <strong>Program Cost:</strong> ฿{(longToMoney(program.programPrice))}
                    </p>
                </div>
                <hr />
            </React.Fragment>
        );
    });


    let totalRoomPrice = 0;

    const rooms = reservationState.RoomBookings.toArray();

    const transformedRooms = {} as Record<string, {
        roomName: string,
        roomNights: number,
        roomCheckInDate: string,
        roomCheckOutDate: string,
        roomPrice: number,
        quantity: number
    }>;

    for (const room of rooms) {
        const roomName = getRoomTypeDisplayName(reservationState, room.RoomType.value);
        const roomPrice = multiplyAndRemoveDecimal(room.CalculatedPrice.value.totalPayableAmount);
        const roomNights = Math.ceil(differenceInHours(room.CheckOut.value, room.CheckIn.value) / 24) | 0;
        const roomCheckInDate = format(room.CheckIn.value, 'MMMM d, yyyy');
        const roomCheckOutDate = format(room.CheckOut.value, 'MMMM d, yyyy');
        const roomStartOfDay = startOfDay(room.CheckIn.value);

        const groupKey = `${roomName}_${roomCheckInDate}_${roomCheckOutDate}`;
        const existingTransformedRoom = transformedRooms[groupKey];

        if (!existingTransformedRoom) {
            transformedRooms[groupKey] = {
                roomName,
                roomNights,
                roomCheckInDate,
                roomCheckOutDate,
                roomPrice,
                quantity: 1
            };
        } else {
            existingTransformedRoom.roomPrice += roomPrice;
            existingTransformedRoom.quantity += 1;
        }

        totalRoomPrice += roomPrice;
    }

    const roomsEle = Object.entries(transformedRooms).map(([, room]) => {
        return (
            <React.Fragment key={room.roomName}>
                <div>
                    <p>
                        {room.quantity <= 1 && (
                            <>
                                <strong>Accommodation:</strong> {room.roomName}
                            </>
                        )}
                        {room.quantity > 1 && (
                            <>
                                <strong>Accommodation:</strong> {room.quantity} x {room.roomName}
                            </>
                        )}
                        <br />
                        <strong>Duration:</strong> {room.roomNights} {room.roomNights === 1 ? 'night' : 'nights'}
                        <br />
                        <strong>Check-in:</strong> {room.roomCheckInDate}
                        <br />
                        <strong>Check-out:</strong> {room.roomCheckOutDate}
                        <br />
                        <strong>Accommodation Cost:</strong> ฿{longToMoney(room.roomPrice)}
                    </p>
                </div>
                <hr />
            </React.Fragment>
        );
    });

    // Calculate totals using longs
    const payableLong = multiplyAndRemoveDecimal(reservationState.AmountPayableTotal.value);
    const dueLong = multiplyAndRemoveDecimal(reservationState.AmountDueTotal.value);
    const totalPaid = payableLong - dueLong; // Now subtracting longs
    const remainderDue = dueLong; // This is already a long after multiplyAndRemoveDecimal

    const posItems = reservationState.PosItems.value;
    const posItemsEle = posItems.length > 0 ? (
        <>
            {posItems.map((item: ReservationPosItem, index: number) => { // Added type
                // Assuming item.Amount is the unit price (long)
                return (
                    <React.Fragment key={`pos_${index}`}>
                        <b>{item.Description}</b> x {item.Quantity}
                        <br />
                        <b>Price:</b> ฿{longToMoney(multiplyAndRemoveDecimal(item.Amount * item.Quantity))}
                        <hr />
                    </React.Fragment>
                );
            })}
            {/* Removed separate POS total line */}
        </>
    ) : null;

    return (
        <div>
            <div style={{ marginBottom: '10px' }}>
                <MacroCopyButtons getHtml={() => rteRef.current?.getHtml() ?? null} />
            </div>
            <RichTextEditorComponent ref={rteRef}>
                {/* Use the state variable for first name */}
                <p>Hi {firstName},</p>
                <br />
                <p>Thank you for choosing us! You're officially on your way to a healthier, stronger you.</p>
                <br />
                <p>
                    We're excited to have you join us.<br /><br />
                </p>
                <p><span style={{ textDecoration: 'underline' }}><strong>To get started with our program, please provide the following details so we can enroll you and set you up in our digital system for goal tracking and food ordering:</strong></span></p>
                <p></p>
                <ul>
                <li><span style={{ color: '#ba372a' }}>Full names of all program participants</span></li>
                <li><span style={{ color: '#ba372a' }}>Preferred email addresses for each participant</span></li>
                <li><span style={{ color: '#ba372a' }}>A WhatsApp phone number for coordinating your arrival and program onboarding</span></li>
                </ul>
                <p></p>
                <p><span style={{ textDecoration: 'underline' }}><strong>Once we receive this information, we’ll send you the next steps in a separate email about a week before your arrival. Please keep an eye out for it!</strong></span></p>
                <br />
                <p>This email confirms that we've received your payment and secured your booking for the selected program,
                    accommodation, and pricing details listed below.</p>
                <br />
                <p>Please review your booking details below:</p>
                <br />
                {/* Display calculated longs */}
                <p>
                    <strong>Deposit received: </strong>{longToTHBWithPrefix(totalPaid)}<br /> {/* Removed CurrencyCode */}
                    <strong>Remainder to be paid: </strong>{longToTHBWithPrefix(remainderDue)}&nbsp; {/* Removed CurrencyCode */}
                </p>
                <hr />
                {programsEle}
                {roomsEle}
                {posItemsEle}
                <p>&nbsp;</p>
                <p>
                    <strong>Standard hotel accommodation check-in / check-out time procedures apply at PhuketFit:</strong><br />&nbsp;
                </p>
                <p>· Check-in Time 14:00 hours</p>
                <p>· Check-out Time 12:00 hours</p>
                <br />
                <hr />
                <br />
                <p>
                    <strong><u>· Please forward your flight arrival details, so we may coordinate your
                        complimentary airport arrival meet-and-greet service to PhuketFit Resort.</u></strong>
                </p>
                <br />
                <p>Should you have any questions or should your travel details change, please let us know and
                    we'll try our best to find the right solution&nbsp;</p>
                <br />
                <p>We're excited to have you joining us, and&nbsp;honoured&nbsp;to be a part of your journey!
                <br /><br />
                    <strong>Our goal is to help you achieve yours!</strong>
                </p>
                <br />
                <p>Healthy regards,</p>
                <br />
                <p>Liam</p>
                <br />
                <hr />
                <br />
                <p>
                    <strong>**IMPORTANT</strong>**<strong> In the unlikely event of an injury, illness, or travel disruption preventing
                        you from starting or completing your program with us, we <u>highly</u> recommend securing a travel insurance
                        policy as a precaution.</strong>
                </p>
                <br />
                <p>
                    PhuketFit terms can be found here: <a href="http://phuketfit.com/terms-and-conditions/">http://phuketfit.com/terms-and-conditions/</a><br />
                    and our disclaimer here: <a href="http://phuketfit.com/disclaimer/">http://phuketfit.com/disclaimer/</a>
                </p>
                <Inject services={[Toolbar, Image, Link, HtmlEditor, QuickToolbar]} />
            </RichTextEditorComponent>
        </div>
    );
}

export interface BookingConfirmationMacroComponentProps {
    reservationState: ReservationState;
}
